// Conservative object-shape proof. Backend admission remains authoritative.
export const isObjectOnlySchema = (schema: Record<string, unknown>): boolean => {
  let remaining = 512;
  const legacyRefSiblings = String(schema.$schema ?? '').includes('/draft-0');
  const object = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);
  const visit = (node: unknown, depth: number, refs: Set<string>): boolean => {
    if (--remaining < 0 || depth > 32 || !object(node)) return false;
    if (depth && ('$id' in node || 'id' in node || '$schema' in node)) return false;
    const refOnly = legacyRefSiblings && '$ref' in node;
    if (
      !refOnly &&
      (node.type === 'object' ||
        (Array.isArray(node.type) && node.type.length === 1 && node.type[0] === 'object'))
    )
      return true;
    for (const keyword of ['oneOf', 'anyOf']) {
      const branches = node[keyword];
      if (
        !refOnly &&
        Array.isArray(branches) &&
        branches.length > 0 &&
        branches.every((branch) => visit(branch, depth + 1, refs))
      )
        return true;
    }
    if (
      !refOnly &&
      Array.isArray(node.allOf) &&
      node.allOf.some((branch) => visit(branch, depth + 1, refs))
    )
      return true;
    const ref = node.$ref;
    if (typeof ref !== 'string' || !ref.startsWith('#/') || ref.includes('%') || refs.has(ref))
      return false;
    let target: unknown = schema;
    for (const segment of ref.slice(2).split('/')) {
      if (/~(?![01])/u.test(segment)) return false;
      if (target !== schema && object(target)) {
        const resource = target;
        if (['$id', 'id', '$schema'].some((key) => typeof resource[key] === 'string')) return false;
      }
      const key = segment.replaceAll('~1', '/').replaceAll('~0', '~');
      if (!object(target) || !Object.hasOwn(target, key)) return false;
      target = target[key];
    }
    return visit(target, depth + 1, new Set([...refs, ref]));
  };
  return visit(schema, 0, new Set());
};
